import { useAuth } from '@agentnet/auth';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { GetFileInfoProps } from 'api/file/file-api';
import { updateFincenPropertiesAPI } from 'api/fincen-report-api';
import { FincenUpdateResponse, PropertyData } from 'features/files/fincen-reporting/types';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import useAsync from 'hooks/useAsync';
import { useAutoSaveNavigation, useFincenAutoSave } from 'hooks/useAutoSave';
import { useNavigationLoading } from 'hooks/useNavigationLoading';
import { useViewState } from 'hooks/ViewStateContext';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AsyncStatus } from '../constants';
import { useMenuContext } from '../ReportingLayout';
import PropertyInformationContent from './PropertyInformationContent';

const PropertyInformationContainer: FC = () => {
  const { addSnackbarMessage } = useSnackBars();
  const { getAccessToken } = useAuth();
  const { fileId } = useParams<{ fileId: string }>();
  const history = useHistory();
  const fileDataCtx = useContext(FileDataContext) as FileDataContextInterface;
  const { fileData } = fileDataCtx;

  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const {
    fincenReport,
    fincenReportStatus,
    updatePropertyInformation,
    propertyAddressValidationErrors,
    clearPropertyAddressValidationErrors,
  } = fincenReportCtx ?? {};

  const { menuCollapsed } = useMenuContext();
  const { setRouterParamValue } = useViewState();

  const generatePropertyId = (index: number): string => `property-${index + 1}`;

  useEffect(() => {
    setRouterParamValue(fileId || '');
  }, [fileId]);
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [shouldNavigateOnComplete, setShouldNavigateOnComplete] = useState(false);
  const isNavigating = useNavigationLoading();

  const handleAutoSave = useCallback(async (): Promise<void> => {
    if (!fincenReport) return;
    if (fincenReport.reportStatusCdId === 9302) return; // Disable auto-save once report generated
    const token = await getAccessToken();
    await updateFincenPropertiesAPI(String(fincenReport.fincenReportId || 0), properties, token);
  }, [fincenReport, getAccessToken, properties]);

  useAutoSaveNavigation('property-information', handleAutoSave, fileId, fincenReport?.reportStatusCdId !== 9302);

  const { triggerSave, isSaving } = useFincenAutoSave({
    data: properties,
    updateFunction: updatePropertyInformation,
    apiSaveFunction: updateFincenPropertiesAPI,
    reportId: fincenReport?.fincenReportId,
    hasContent: (data) => data.some((p) => p.address && (p.address.address1 || p.address.city)),
    enabled: fincenReport?.reportStatusCdId !== 9302,
    errorContext: 'property information',
  });

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      contentContainer: {
        paddingBottom: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
      },
      mainContainer: {
        width: '100%',
        position: 'relative',
      },
      contentWrapper: {
        overflow: 'auto',
        paddingBottom: theme.spacing(8),
      },
    }),
  );

  const classes = useStyles();

  useEffect(() => {
    if (fileDataCtx && !fileDataCtx.fileData && fileDataCtx.loadFileData) {
      const fileProps: GetFileInfoProps = {
        fileId,
      };
      fileDataCtx.loadFileData(fileProps);
    }
  }, [fileId, fileDataCtx]);

  useEffect(() => {
    if (formSubmitted) {
      executeUpdateFincenPropertiesData();
    }
  }, [formSubmitted]);

  const updateFincenPropertiesData = async (): Promise<FincenUpdateResponse> => {
    const token = await getAccessToken();
    return updateFincenPropertiesAPI(String(fincenReport?.fincenReportId || 0), properties, token);
  };
  const {
    execute: executeUpdateFincenPropertiesData,
    value: updateFincenPropertiesResponse,
    status: updateFincenPropertiesStatus,
    errors: updateFincenPropertiesErrors,
  } = useAsync<FincenUpdateResponse>(updateFincenPropertiesData, false);

  useEffect(() => {
    if (updateFincenPropertiesStatus === AsyncStatus.SUCCESS) {
      if (shouldNavigateOnComplete) {
        setShouldNavigateOnComplete(false);
        history.push(`/files/${fileId}/fincen-reporting/transferee-info`);
      } else {
        addSnackbarMessage({
          message: 'Saving Successful',
          type: 'success',
        });
      }
      fincenReportCtx?.reloadFincenReport?.(fileData?.fileId ?? '0');
      setFormSubmitted(false);
    }
  }, [updateFincenPropertiesStatus, shouldNavigateOnComplete]);

  const handleSubmissionComplete = (forceNavigate?: boolean) => {
    setFormSubmitted(false);
    if (forceNavigate || shouldNavigateOnComplete) {
      setShouldNavigateOnComplete(false);
      history.push(`/files/${fileId}/fincen-reporting/transferee-info`);
    }
  };

  const handlePropertiesRefresh = () => {
    if (fincenReport?.propertyAddresses) {
      const mappedProperties = fincenReport.propertyAddresses.map((address, index) => ({
        id: generatePropertyId(index),
        address: address,
      }));
      setProperties(mappedProperties);
    }
  };

  useEffect(() => {
    handlePropertiesRefresh();
  }, [fincenReport?.propertyAddresses]);

  const handleAddProperty = () => {
    const newProperties = [
      ...properties,
      {
        id: generatePropertyId(properties.length),
        address: {},
      },
    ];
    setProperties(newProperties);
    if (updatePropertyInformation) {
      updatePropertyInformation(newProperties);
    }
  };

  const handleSave = () => {
    setFormSubmitted(true);
  };

  const handleBack = () => {
    history.push(`/files/${fileId}/fincen-reporting/report-party`);
  };

  const handleNext = () => {
    if (fincenReport?.reportStatusCdId === 9302) {
      history.push(`/files/${fileId}/fincen-reporting/transferee-info`);
      return;
    }
    setShouldNavigateOnComplete(true);
    setFormSubmitted(true);
  };
  const handlePropertyChange = (index: number, updatedProperty: PropertyData) => {
    const updatedProperties = [...properties];
    updatedProperties[index] = updatedProperty;
    setProperties(updatedProperties);

    if (updatePropertyInformation) {
      updatePropertyInformation(updatedProperties);
    }
  };

  const handleDeleteProperty = (index: number, id: string) => {
    const updatedProperties = properties.filter((_, idx) => idx !== index);
    setProperties(updatedProperties);
    if (updatePropertyInformation) {
      updatePropertyInformation(updatedProperties);
    }
  };

  if (!fileId) {
    return <Redirect to="/files" />;
  }

  return (
    <div data-testid="fincen-property-information-container" className={classes.mainContainer}>
      <LoadingSpinner
        status={formSubmitted || isNavigating || fincenReportStatus === 'pending' ? 'pending' : 'success'}
        variant="linear"
      />
      <Grid container spacing={0}>
        <Grid item xs className={classes.contentWrapper}>
          <div className={classes.contentContainer}>
            <PropertyInformationContent
              properties={properties}
              status="success"
              onRefresh={handlePropertiesRefresh}
              onAddProperty={handleAddProperty}
              onPropertyChange={handlePropertyChange}
              onDeleteProperty={handleDeleteProperty}
              fileData={fileData}
              menuCollapsed={menuCollapsed}
              formSubmitted={formSubmitted}
              isReadOnly={fincenReport?.reportStatusCdId === 9302}
            />
          </div>
        </Grid>
      </Grid>
      <BottomAppBar
        cancelLabel="Back"
        onCancel={handleBack}
        cancelDataQa="fincen-property-back-button"
        secondaryActionLabel="Save Report"
        onSecondaryClick={handleSave}
        secondaryActionDataQa="fincen-property-save-button"
        disableSecondary={properties.length === 0 || fincenReport?.reportStatusCdId === 9302}
        primaryActionLabel="Next"
        onPrimaryClick={handleNext}
        primaryActionDataQa="fincen-property-next-button"
      />
    </div>
  );
};

export default PropertyInformationContainer;
